import Phaser from 'phaser';
import {
  ATTACKER_SIZE,
  FONT_FAMILY,
  GAME_WIDTH,
  getQueueY,
  getQueueLaneWidth,
  getTotalLanesWidth,
  getLanesStartX,
  QUEUE_LANE_GAP,
  QUEUE_ROW_SPACING,
  MAX_VISIBLE_QUEUE_ROWS,
} from '../config/constants';
import { getGameplayColorHex } from '../config/colors';
import { QueueSystem } from '../systems/QueueSystem';
import type { AttackerData } from '../entities/Attacker';
import { isFrozen } from '../entities/Attacker';
import { AttackerRenderer } from './AttackerRenderer';
import { crispText } from '../ui/textUtils';
import { soundManager } from '../audio/SoundManager';

/**
 * Owns all queue-related visuals: lane backgrounds, attacker icons,
 * frozen ice blocks, mystery orb reveal animations, queue shift animations,
 * and thaw effects.
 *
 * GameScene delegates queue display drawing and animations to this class.
 */
export class QueueRenderer {
  private scene: Phaser.Scene;
  private queue: QueueSystem;
  private attackerRenderer: AttackerRenderer;

  /** Static lane background graphics (created once, never destroyed during gameplay). */
  private laneBgs: Phaser.GameObjects.Graphics[] = [];
  /** Whether static lane backgrounds have been created. */
  private laneBgsCreated = false;

  /** Dynamic sprites/graphics created for queue attacker display (destroyed on redraw). */
  queueLaneSprites: Phaser.GameObjects.GameObject[] = [];
  /** Hit areas for the queue lanes (rebuilt on redraw). */
  queueLaneHitAreas: { x: number; y: number; w: number; h: number; lane: number }[] = [];

  /** Mystery orb reveal tracking: Set of "attackerId:orbIndex" currently animating reveal. */
  /** Typed metadata for queue sprite GameObjects, replacing untyped getData/setData. */
  private readonly spriteAttackerIds = new Map<Phaser.GameObjects.GameObject, number>();
  private readonly spriteOrbIndices = new Map<Phaser.GameObjects.GameObject, number>();

  /** Typed metadata for connector Graphics objects. */
  private readonly connectorMeta = new Map<Phaser.GameObjects.Graphics, {
    thickness: number;
    alpha: number;
    baseAlpha: number;
  }>();
  /** Set of Graphics objects that are connectors (replaces isConnector data flag). */
  private readonly connectorSet = new Set<Phaser.GameObjects.Graphics>();

  revealingMysteryOrbs = new Set<string>();

  constructor(scene: Phaser.Scene, queue: QueueSystem, attackerRenderer: AttackerRenderer) {
    this.scene = scene;
    this.queue = queue;
    this.attackerRenderer = attackerRenderer;
  }

  /** Reset mutable state (called from GameScene.init). */
  reset(): void {
    this.queueLaneSprites = [];
    this.queueLaneHitAreas = [];
    this.revealingMysteryOrbs.clear();
    this.clearSpriteMeta();
    // Destroy static backgrounds so they're recreated for the new level
    for (const bg of this.laneBgs) bg.destroy();
    this.laneBgs = [];
    this.laneBgsCreated = false;
  }

  /** Clear all typed sprite metadata maps. */
  private clearSpriteMeta(): void {
    this.spriteAttackerIds.clear();
    this.spriteOrbIndices.clear();
    this.connectorMeta.clear();
    this.connectorSet.clear();
  }

  // ── Static lane background (drawn once) ──────────────────────────────

  /** Ensure static lane backgrounds exist. Creates them on first call. */
  private ensureLaneBgs(): { lanesStartX: number; laneY: number; laneHeight: number } {
    const laneWidth = getQueueLaneWidth(this.queue.numLanes());
    const laneGap = QUEUE_LANE_GAP;
    const totalLanesWidth = this.queue.numLanes() * laneWidth + (this.queue.numLanes() - 1) * laneGap;
    const lanesStartX = (GAME_WIDTH - totalLanesWidth) / 2;
    const laneY = getQueueY();
    const rowSpacing = QUEUE_ROW_SPACING;
    const laneHeight = 56 + MAX_VISIBLE_QUEUE_ROWS * rowSpacing + 40;

    if (!this.laneBgsCreated) {
      this.queueLaneHitAreas = [];
      for (let lane = 0; lane < this.queue.numLanes(); lane++) {
        const laneX = lanesStartX + lane * (laneWidth + laneGap);
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x1a1a30, 0.6);
        bg.fillRoundedRect(laneX, laneY, laneWidth, laneHeight, 16);
        bg.lineStyle(2, 0x3a3a5a, 0.5);
        bg.strokeRoundedRect(laneX, laneY, laneWidth, laneHeight, 16);
        this.laneBgs.push(bg);
        this.queueLaneHitAreas.push({ x: laneX, y: laneY, w: laneWidth, h: laneHeight, lane });
      }
      this.laneBgsCreated = true;
    }

    return { lanesStartX, laneY, laneHeight };
  }

  // ── Queue display ────────────────────────────────────────────────────

  drawQueueDisplay(): void {
    // Destroy only dynamic sprites (not static lane backgrounds)
    for (const obj of this.queueLaneSprites) obj.destroy();
    this.queueLaneSprites = [];
    this.clearSpriteMeta();

    const laneWidth = getQueueLaneWidth(this.queue.numLanes());
    const laneGap = QUEUE_LANE_GAP;
    const { lanesStartX, laneY, laneHeight } = this.ensureLaneBgs();
    const rowSpacing = QUEUE_ROW_SPACING;

    const rows = this.queue.getRows();
    if (rows.length === 0) {
      // Draw empty indicators
      for (let lane = 0; lane < this.queue.numLanes(); lane++) {
        const laneX = lanesStartX + lane * (laneWidth + laneGap);
        const laneCenterX = laneX + laneWidth / 2;
        const empty = crispText(this.scene.add.text(laneCenterX, laneY + laneHeight / 2, '-', {
          fontFamily: FONT_FAMILY,
          fontSize: '32px',
          color: '#333355',
        }), false).setOrigin(0.5);
        this.queueLaneSprites.push(empty);
      }
      return;
    }

    // Draw attackers row by row (limit to MAX_VISIBLE_QUEUE_ROWS + 1 fade row)
    const drawnIds = new Set<number>();
    const maxVisibleRows = MAX_VISIBLE_QUEUE_ROWS + 1; // +1 for faded hint row

    for (let rowIdx = 0; rowIdx < Math.min(rows.length, maxVisibleRows); rowIdx++) {
      const row = rows[rowIdx];
      const rowY = laneY + 64 + rowIdx * rowSpacing;
      const isFrontRow = rowIdx === 0;
      const isFadeRow = rowIdx >= MAX_VISIBLE_QUEUE_ROWS; // row 3+ fades out
      const scale = isFrontRow ? 1.15 : 0.85;
      const baseAlpha = isFrontRow ? 1.0 : (isFadeRow ? 0.2 : 0.55);

      for (let lane = 0; lane < this.queue.numLanes(); lane++) {
        const attacker = row[lane];
        if (!attacker) continue;
        if (drawnIds.has(attacker.id)) continue;
        drawnIds.add(attacker.id);

        const spanLanes = attacker.queueLanes ?? [lane];
        const isDispatchable = isFrontRow && this.queue.peekQueue(lane) !== null;
        const frozen = isFrozen(attacker);

        // ── Hammer item: render as hammer icon ───────────────────────────
        if (attacker.isHammer) {
          const laneX = lanesStartX + lane * (laneWidth + laneGap);
          const laneCenterX = laneX + laneWidth / 2;
          const hammerC = this.scene.add.container(laneCenterX, rowY).setDepth(5);

          // Hammer head (gray)
          const headG = this.scene.add.graphics();
          headG.fillStyle(0x888888, 1);
          headG.fillRoundedRect(-18, -26, 36, 26, 6);
          headG.lineStyle(2, 0x666666, 1);
          headG.strokeRoundedRect(-18, -26, 36, 26, 6);
          hammerC.add(headG);

          // Hammer handle (brown)
          const handleG = this.scene.add.graphics();
          handleG.fillStyle(0x8B6914, 1);
          handleG.fillRect(-5, 0, 10, 30);
          hammerC.add(handleG);

          // Subtle pulse glow
          const glow = this.scene.add.graphics();
          glow.fillStyle(0xFFDD44, 0.15);
          glow.fillCircle(0, 0, 36);
          hammerC.addAt(glow, 0);

          hammerC.setScale(scale);
          if (!isFrontRow) hammerC.setAlpha(baseAlpha);

          // Pulse animation for front-row hammers
          if (isFrontRow) {
            this.scene.tweens.add({
              targets: hammerC,
              scaleX: scale * 1.15,
              scaleY: scale * 1.15,
              duration: 500,
              yoyo: true,
              repeat: -1,
              ease: 'Sine.easeInOut',
            });
          }

          this.queueLaneSprites.push(hammerC);
          continue;
        }

        if (frozen) {
          // ── Frozen attacker: render as ice block ──────────────────────
          if (spanLanes.length > 1) {
            // Multi-lane frozen attacker: wide ice block spanning lanes with per-orb depth
            const orbPositions: { x: number; y: number }[] = [];
            for (let i = 0; i < spanLanes.length; i++) {
              const orbLaneX = lanesStartX + spanLanes[i] * (laneWidth + laneGap);
              const orbCenterX = orbLaneX + laneWidth / 2;
              const orbDepth = this.queue.getDepth(attacker.id, spanLanes[i]);
              const orbY = laneY + 64 + (orbDepth >= 0 ? orbDepth : rowIdx) * rowSpacing;
              orbPositions.push({ x: orbCenterX, y: orbY });
            }
            const centerX = (orbPositions[0].x + orbPositions[orbPositions.length - 1].x) / 2;
            const centerY = (orbPositions[0].y + orbPositions[orbPositions.length - 1].y) / 2;
            const iceWidth = (orbPositions[orbPositions.length - 1].x - orbPositions[0].x) + ATTACKER_SIZE * 1.2;
            const iceHeight = ATTACKER_SIZE * 1.2 + Math.abs(orbPositions[orbPositions.length - 1].y - orbPositions[0].y);
            // Determine scale/alpha from whether all orbs are at depth 0
            const allAtFront = spanLanes.every(l => this.queue.getDepth(attacker.id, l) === 0);
            const maxOrbDepth = Math.max(...spanLanes.map(l => this.queue.getDepth(attacker.id, l)));
            const frozenIsFadeRow = maxOrbDepth >= MAX_VISIBLE_QUEUE_ROWS;
            const frozenScale = allAtFront ? 1.15 : 0.85;
            const frozenAlpha = allAtFront ? 1.0 : (frozenIsFadeRow ? 0.2 : 0.55);
            const iceBlock = this.createFrozenVisual(centerX, centerY, iceWidth, iceHeight, attacker.frozen!, frozenScale);
            this.spriteAttackerIds.set(iceBlock, attacker.id);
            if (!allAtFront) iceBlock.setAlpha(frozenAlpha);
            this.queueLaneSprites.push(iceBlock);
          } else {
            // Single-lane frozen attacker
            const laneX = lanesStartX + lane * (laneWidth + laneGap);
            const laneCenterX = laneX + laneWidth / 2;
            const iceSize = ATTACKER_SIZE * 1.2;
            const iceBlock = this.createFrozenVisual(laneCenterX, rowY, iceSize, iceSize, attacker.frozen!, scale);
            this.spriteAttackerIds.set(iceBlock, attacker.id);
            if (!isFrontRow) iceBlock.setAlpha(baseAlpha);
            this.queueLaneSprites.push(iceBlock);
          }
        } else if (spanLanes.length > 1) {
          // Multi-lane attacker: draw connector and each orb with per-orb depth
          const orbPositions: { x: number; y: number }[] = [];
          const orbDepths: number[] = [];
          for (let i = 0; i < spanLanes.length; i++) {
            const orbLaneX = lanesStartX + spanLanes[i] * (laneWidth + laneGap);
            const orbCenterX = orbLaneX + laneWidth / 2;
            const orbDepth = this.queue.getDepth(attacker.id, spanLanes[i]);
            const effectiveDepth = orbDepth >= 0 ? orbDepth : rowIdx;
            orbDepths.push(effectiveDepth);
            const orbY = laneY + 64 + effectiveDepth * rowSpacing;
            orbPositions.push({ x: orbCenterX, y: orbY });
          }

          // Determine scale/alpha from whether all orbs are at depth 0
          const allAtFront = orbDepths.every(d => d === 0);
          const maxOrbDepth = Math.max(...orbDepths);
          const multiIsFadeRow = maxOrbDepth >= MAX_VISIBLE_QUEUE_ROWS;
          const multiScale = allAtFront ? 1.15 : 0.85;
          const multiBaseAlpha = allAtFront ? 1.0 : (multiIsFadeRow ? 0.2 : 0.55);
          const multiIsDispatchable = allAtFront && this.queue.peekQueue(lane) !== null;

          // Draw dynamic connector line between orb positions
          const barGraphics = this.scene.add.graphics();
          this.spriteAttackerIds.set(barGraphics, attacker.id);
          this.connectorSet.add(barGraphics);
          const barThickness = allAtFront ? 6 : 4;
          const barAlpha = 1.0;
          this.connectorMeta.set(barGraphics, {
            thickness: barThickness,
            alpha: barAlpha,
            baseAlpha: 1.0,
          });
          barGraphics.lineStyle(barThickness, 0x8888aa, barAlpha);

          // Draw line segments between consecutive orbs (now diagonal when depths differ)
          for (let i = 0; i < orbPositions.length - 1; i++) {
            const p1 = orbPositions[i];
            const p2 = orbPositions[i + 1];
            barGraphics.beginPath();
            barGraphics.moveTo(p1.x, p1.y);
            barGraphics.lineTo(p2.x, p2.y);
            barGraphics.strokePath();
          }
          barGraphics.setAlpha(1.0);
          this.queueLaneSprites.push(barGraphics);

          // Draw each orb at its per-depth position
          for (let i = 0; i < spanLanes.length; i++) {
            const orb = attacker.orbs[i];
            if (!orb) continue;
            const pos = orbPositions[i];
            // Per-orb scale/alpha based on individual depth
            const orbAtFront = orbDepths[i] === 0;
            const orbIsFade = orbDepths[i] >= MAX_VISIBLE_QUEUE_ROWS;
            const orbScale = orbAtFront ? 1.15 : 0.85;
            const orbAlpha = orbAtFront ? (multiIsDispatchable ? 1.0 : 0.45) : (orbIsFade ? 0.2 : 0.55);
            const vis = this.attackerRenderer.createAttackerVisual(pos.x, pos.y, [orb]);
            vis.setScale(orbScale);
            this.spriteAttackerIds.set(vis, attacker.id);
            this.spriteOrbIndices.set(vis, i);
            vis.setAlpha(orbAlpha);
            this.queueLaneSprites.push(vis);
          }

          // Show lock icon when blocked (front row only, at each orb's position)
          if (allAtFront && !multiIsDispatchable) {
            for (const pos of orbPositions) {
              const lock = crispText(this.scene.add.text(pos.x, pos.y + 28, '\u25CB', {
                fontFamily: FONT_FAMILY,
                fontSize: '18px',
                color: '#666688',
              }), false).setOrigin(0.5);
              this.queueLaneSprites.push(lock);
            }
          }
        } else {
          // Single-lane attacker
          const laneX = lanesStartX + lane * (laneWidth + laneGap);
          const laneCenterX = laneX + laneWidth / 2;
          const vis = this.attackerRenderer.createAttackerVisual(laneCenterX, rowY, attacker.orbs);
          vis.setScale(scale);
          this.spriteAttackerIds.set(vis, attacker.id);
          if (!isDispatchable && isFrontRow) vis.setAlpha(0.45);
          if (!isFrontRow) vis.setAlpha(baseAlpha);
          this.queueLaneSprites.push(vis);

          if (isFrontRow && !isDispatchable) {
            const lock = crispText(this.scene.add.text(laneCenterX, rowY + 28, '\u25CB', {
              fontFamily: FONT_FAMILY,
              fontSize: '18px',
              color: '#666688',
            }), false).setOrigin(0.5);
            this.queueLaneSprites.push(lock);
          }
        }
      }
    }

    // Count badges per lane
    for (let lane = 0; lane < this.queue.numLanes(); lane++) {
      const laneX = lanesStartX + lane * (laneWidth + laneGap);
      const laneCenterX = laneX + laneWidth / 2;
      const q = this.queue.getQueueLane(lane);
      if (q.length > 0) {
        const uniqueIds = new Set<number>();
        for (const a of q) uniqueIds.add(a.id);
        const countLabel = crispText(this.scene.add.text(laneCenterX, laneY + laneHeight - 20, `${uniqueIds.size}`, {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: '#6666aa',
        }), false).setOrigin(0.5);
        this.queueLaneSprites.push(countLabel);
      } else {
        const empty = crispText(this.scene.add.text(laneCenterX, laneY + laneHeight / 2, '-', {
          fontFamily: FONT_FAMILY,
          fontSize: '32px',
          color: '#333355',
        }), false).setOrigin(0.5);
        this.queueLaneSprites.push(empty);
      }
    }
  }

  // ── Queue shift animation ──────────────────────────────────────────

  /**
   * Snapshot the visual positions of all queue attacker sprites (by attacker ID + orbIndex).
   * Used before a dispatch so we can animate remaining items sliding into their new positions.
   */
  snapshotQueuePositions(): Map<string, { x: number; y: number; scaleX: number; scaleY: number; alpha: number }> {
    const snapshot = new Map<string, { x: number; y: number; scaleX: number; scaleY: number; alpha: number }>();
    for (const obj of this.queueLaneSprites) {
      if (!(obj instanceof Phaser.GameObjects.Container)) continue;
      const aid = this.spriteAttackerIds.get(obj);
      if (aid == null) continue;
      const orbIdx = this.spriteOrbIndices.get(obj);
      const key = orbIdx != null ? `${aid}:${orbIdx}` : `${aid}`;
      snapshot.set(key, { x: obj.x, y: obj.y, scaleX: obj.scaleX, scaleY: obj.scaleY, alpha: obj.alpha });
    }
    return snapshot;
  }

  /**
   * Redraw the queue display, then animate any sprites that moved from their old positions
   * (captured in the snapshot) to their new positions.
   */
  animateQueueShift(oldSnapshot: Map<string, { x: number; y: number; scaleX: number; scaleY: number; alpha: number }>): void {
    this.drawQueueDisplay();

    // Build a map of attacker ID -> orb containers for connector redrawing
    const orbContainersByAttacker = new Map<number, Phaser.GameObjects.Container[]>();
    // Collect connector graphics by attacker ID
    const connectorsByAttacker = new Map<number, Phaser.GameObjects.Graphics>();

    for (const obj of this.queueLaneSprites) {
      if (obj instanceof Phaser.GameObjects.Container) {
        const aid = this.spriteAttackerIds.get(obj);
        if (aid != null) {
          if (!orbContainersByAttacker.has(aid)) orbContainersByAttacker.set(aid, []);
          orbContainersByAttacker.get(aid)!.push(obj);
        }
      } else if (obj instanceof Phaser.GameObjects.Graphics) {
        const isConnector = this.connectorSet.has(obj);
        const aid = this.spriteAttackerIds.get(obj);
        if (isConnector && aid != null) {
          connectorsByAttacker.set(aid, obj);
        }
      }
    }

    // Track which attacker IDs have animated orbs (need connector redraw)
    const animatedAttackerIds = new Set<number>();

    for (const obj of this.queueLaneSprites) {
      if (!(obj instanceof Phaser.GameObjects.Container)) continue;
      const aid = this.spriteAttackerIds.get(obj);
      if (aid == null) continue;
      const orbIdx = this.spriteOrbIndices.get(obj);
      const key = orbIdx != null ? `${aid}:${orbIdx}` : `${aid}`;
      const old = oldSnapshot.get(key);
      if (!old) continue;

      const newX = obj.x;
      const newY = obj.y;
      const newScaleX = obj.scaleX;
      const newScaleY = obj.scaleY;
      const newAlpha = obj.alpha;

      // Only animate if position or scale actually changed
      if (Math.abs(old.x - newX) < 1 && Math.abs(old.y - newY) < 1 &&
          Math.abs(old.scaleX - newScaleX) < 0.01 && Math.abs(old.scaleY - newScaleY) < 0.01 &&
          Math.abs(old.alpha - newAlpha) < 0.01) continue;

      animatedAttackerIds.add(aid);

      // Start from old position, tween to new
      obj.setPosition(old.x, old.y);
      obj.setScale(old.scaleX, old.scaleY);
      obj.setAlpha(old.alpha);

      this.scene.tweens.add({
        targets: obj,
        x: newX,
        y: newY,
        scaleX: newScaleX,
        scaleY: newScaleY,
        alpha: newAlpha,
        duration: 250,
        ease: 'Sine.easeOut',
      });
    }

    // For each animated attacker that has a connector, redraw the connector each frame
    for (const aid of animatedAttackerIds) {
      const connector = connectorsByAttacker.get(aid);
      const orbs = orbContainersByAttacker.get(aid);
      if (!connector || !orbs || orbs.length < 2) continue;

      const meta = this.connectorMeta.get(connector);
      const thickness = meta?.thickness ?? 4;
      const lineAlpha = meta?.alpha ?? 0.3;
      const baseAlpha = meta?.baseAlpha ?? 1.0;

      // Sort orbs by orbIndex to maintain correct ordering
      orbs.sort((a, b) => {
        const ai = this.spriteOrbIndices.get(a) ?? 0;
        const bi = this.spriteOrbIndices.get(b) ?? 0;
        return ai - bi;
      });

      // Redraw connector to match current orb positions using a scene update listener
      const redrawConnector = () => {
        connector.clear();
        connector.lineStyle(thickness, 0x8888aa, lineAlpha);
        for (let i = 0; i < orbs.length - 1; i++) {
          const p1 = orbs[i];
          const p2 = orbs[i + 1];
          connector.beginPath();
          connector.moveTo(p1.x, p1.y);
          connector.lineTo(p2.x, p2.y);
          connector.strokePath();
        }
        connector.setAlpha(1.0);
      };

      // Immediately redraw to start position
      redrawConnector();

      // Add a temporary scene update event that redraws each frame during the tween
      const updateHandler = () => { redrawConnector(); };
      this.scene.events.on('update', updateHandler);

      // Remove the update handler after the tween duration
      this.scene.time.delayedCall(260, () => {
        this.scene.events.off('update', updateHandler);
        // Final redraw to ensure connector is at exact final position
        redrawConnector();
      });
    }
  }

  // ── Frozen attacker visuals ─────────────────────────────────────────

  /** Create a frozen ice block visual (container) for queue display. */
  createFrozenVisual(x: number, y: number, width: number, height: number, counter: number, scale: number): Phaser.GameObjects.Container {
    const children: Phaser.GameObjects.GameObject[] = [];

    // Ice block background -- light blue rounded rectangle
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x88ccff, 0.25);
    bg.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
    // Frost border
    bg.lineStyle(4, 0xaaddff, 0.6);
    bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);
    // Inner frost highlight
    bg.lineStyle(2, 0xcceeFF, 0.3);
    bg.strokeRoundedRect(-width / 2 + 6, -height / 2 + 6, width - 12, height - 12, 8);
    children.push(bg);

    // Frost texture -- small crystals drawn as tiny lines
    const crystals = this.scene.add.graphics();
    crystals.lineStyle(2, 0xcceeFF, 0.35);
    // Draw a few frost lines
    const hw = width / 2;
    const hh = height / 2;
    crystals.beginPath();
    crystals.moveTo(-hw * 0.4, -hh * 0.6);
    crystals.lineTo(-hw * 0.1, -hh * 0.2);
    crystals.moveTo(hw * 0.3, -hh * 0.5);
    crystals.lineTo(hw * 0.1, -hh * 0.1);
    crystals.moveTo(-hw * 0.5, hh * 0.3);
    crystals.lineTo(-hw * 0.2, hh * 0.1);
    crystals.moveTo(hw * 0.4, hh * 0.4);
    crystals.lineTo(hw * 0.15, hh * 0.15);
    crystals.strokePath();
    children.push(crystals);

    // Defrost counter text -- prominent, centered
    const counterText = crispText(this.scene.add.text(0, 0, `${counter}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#2266aa',
      strokeThickness: 6,
    })).setOrigin(0.5);
    children.push(counterText);

    // Snowflake icon below counter
    const flake = crispText(this.scene.add.text(0, 24, '*', {
      fontFamily: FONT_FAMILY,
      fontSize: '20px',
      color: '#aaddff',
    }), false).setOrigin(0.5);
    children.push(flake);

    const container = this.scene.add.container(x, y, children);
    container.setScale(scale);

    // Subtle shimmer/pulse animation (alpha oscillation)
    this.scene.tweens.add({
      targets: container,
      alpha: { from: 0.85, to: 1.0 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return container;
  }

  /** Handle thawed attackers: ice shatter + reveal animation, then redraw queue. */
  handleFrozenThaw(thawedAttackers: AttackerData[]): void {
    // Find positions of thawed attackers in queue for animation
    const rows = this.queue.getRows();
    const laneWidth = getQueueLaneWidth(this.queue.numLanes());
    const laneGap = QUEUE_LANE_GAP;
    const totalLanesWidth = this.queue.numLanes() * laneWidth + (this.queue.numLanes() - 1) * laneGap;
    const lanesStartX = (GAME_WIDTH - totalLanesWidth) / 2;
    const laneY = getQueueY();
    const rowSpacing = QUEUE_ROW_SPACING;

    const thawedIds = new Set(thawedAttackers.map(a => a.id));

    // Find the visual positions of thawed attackers
    for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
      const row = rows[rowIdx];
      for (let lane = 0; lane < this.queue.numLanes(); lane++) {
        const attacker = row[lane];
        if (!attacker || !thawedIds.has(attacker.id)) continue;
        thawedIds.delete(attacker.id); // process each only once

        const spanLanes = attacker.queueLanes ?? [lane];
        let thawX: number;
        let thawY: number;

        if (spanLanes.length > 1) {
          const positions = spanLanes.map(l => {
            const lx = lanesStartX + l * (laneWidth + laneGap) + laneWidth / 2;
            const depth = this.queue.getDepth(attacker.id, l);
            const ly = laneY + 26 + depth * rowSpacing;
            return { x: lx, y: ly };
          });
          thawX = (positions[0].x + positions[positions.length - 1].x) / 2;
          thawY = positions[0].y;
        } else {
          thawX = lanesStartX + lane * (laneWidth + laneGap) + laneWidth / 2;
          thawY = laneY + 32 + rowIdx * rowSpacing;
        }

        // Ice shatter particle burst
        const emitter = this.scene.add.particles(thawX, thawY, 'particle', {
          speed: { min: 40, max: 120 },
          scale: { start: 0.8, end: 0 },
          alpha: { start: 0.9, end: 0 },
          lifespan: 500,
          tint: 0x88ccff,
          emitting: false,
        });
        emitter.explode(12);
        this.scene.time.delayedCall(600, () => emitter.destroy());

        // Brief flash at thaw location
        const flash = this.scene.add.graphics();
        flash.fillStyle(0xaaddff, 0.5);
        flash.fillCircle(thawX, thawY, ATTACKER_SIZE * 0.8);
        flash.setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 300,
          ease: 'Power2',
          onComplete: () => flash.destroy(),
        });
      }
    }

    // Redraw queue display to show newly revealed attackers
    soundManager.cubeDestroy(); // satisfying crack sound
    const snapshot = this.snapshotQueuePositions();
    this.drawQueueDisplay();

    // Animate the newly thawed attackers popping in with Back.easeOut
    for (const obj of this.queueLaneSprites) {
      if (!(obj instanceof Phaser.GameObjects.Container)) continue;
      const aid = this.spriteAttackerIds.get(obj);
      if (aid == null) continue;
      const thawedAttacker = thawedAttackers.find(a => a.id === aid);
      if (!thawedAttacker) continue;

      // Pop-in: scale from 0 to current
      const targetScaleX = obj.scaleX;
      const targetScaleY = obj.scaleY;
      obj.setScale(0);
      this.scene.tweens.add({
        targets: obj,
        scaleX: targetScaleX,
        scaleY: targetScaleY,
        duration: 400,
        ease: 'Back.easeOut',
      });
    }
  }

  /**
   * Visual feedback for an auto-forced dispatch of a frozen attacker when the
   * player is stuck with every queue front frozen. Shows a brief "Forced!"
   * label floating up from the queue lane with a quick flash, accompanied by a
   * short cyan flash behind the affected lane front.
   */
  showForcedDispatch(laneIndex: number, geo?: import('../config/BoardGeometry').BoardGeometry): void {
    const nq = this.queue.numLanes();
    const laneW = getQueueLaneWidth(nq);
    const lanesStartX = getLanesStartX(nq);
    const cx = lanesStartX + laneIndex * (laneW + QUEUE_LANE_GAP) + laneW / 2;
    const baseY = getQueueY(geo) + 12;

    const label = crispText(this.scene.add.text(cx, baseY, 'Forced!', {
      fontFamily: FONT_FAMILY,
      fontSize: '18px',
      color: '#ffd755',
      stroke: '#552200',
      strokeThickness: 3,
    }));
    label.setOrigin(0.5, 0.5);
    label.setDepth(10000);
    label.setAlpha(0);

    // Fade in quickly, float up, fade out
    this.scene.tweens.add({
      targets: label,
      alpha: { from: 0, to: 1 },
      duration: 120,
      ease: 'Power1',
    });
    this.scene.tweens.add({
      targets: label,
      y: baseY - 36,
      duration: 700,
      ease: 'Quad.easeOut',
    });
    this.scene.tweens.add({
      targets: label,
      alpha: 0,
      delay: 420,
      duration: 280,
      ease: 'Power1',
      onComplete: () => label.destroy(),
    });
  }

  /** Shake the frozen attacker sprite when tapped (visual feedback). */
  shakeFrozenAttacker(laneIndex: number): void {
    // Find the frozen attacker sprite in queueLaneSprites for the given lane
    const q = this.queue.getQueueLane(laneIndex);
    if (q.length === 0) return;
    const frozenAttacker = q[0];
    if (!frozenAttacker) return;

    for (const obj of this.queueLaneSprites) {
      if (!(obj instanceof Phaser.GameObjects.Container)) continue;
      const aid = this.spriteAttackerIds.get(obj);
      if (aid === frozenAttacker.id) {
        // Quick horizontal shake
        const origX = obj.x;
        this.scene.tweens.add({
          targets: obj,
          x: origX + 4,
          duration: 40,
          yoyo: true,
          repeat: 3,
          ease: 'Sine.easeInOut',
          onComplete: () => obj.setX(origX),
        });
        break;
      }
    }
  }

  // ── Mystery orb reveal ──────────────────────────────────────────────

  /**
   * Check front-of-queue mystery orbs and reveal them with animation.
   * Called every frame from update(). Reveals mystery orbs when they reach
   * position 0 in their lane and are not frozen.
   */
  checkAndRevealFrontMysteryOrbs(): void {
    const rows = this.queue.getRows();
    if (rows.length === 0) return;

    const frontRow = rows[0];
    const revealed: { attacker: AttackerData; orbIndex: number; lane: number }[] = [];

    for (let lane = 0; lane < this.queue.numLanes(); lane++) {
      const attacker = frontRow[lane];
      if (!attacker) continue;
      if (isFrozen(attacker)) continue;
      if (attacker.isHammer) continue;

      const spanLanes = attacker.queueLanes ?? [lane];

      for (let oi = 0; oi < attacker.orbs.length; oi++) {
        const orb = attacker.orbs[oi];
        if (!orb.mystery) continue;

        const key = `${attacker.id}:${oi}`;
        if (this.revealingMysteryOrbs.has(key)) continue;

        // For multi-orb: only reveal this orb if its lane is at position 0
        const orbLane = spanLanes[oi] ?? lane;
        const depth = this.queue.getDepth(attacker.id, orbLane);
        if (depth !== 0) continue;

        // Mark as revealing to prevent double-trigger
        this.revealingMysteryOrbs.add(key);

        // Permanently reveal the orb data
        orb.mystery = false;

        revealed.push({ attacker, orbIndex: oi, lane: orbLane });
      }
    }

    if (revealed.length === 0) return;

    // Redraw queue to update visuals with revealed colors
    this.drawQueueDisplay();

    // Now find the newly drawn containers and apply reveal animation
    const laneWidth = getQueueLaneWidth(this.queue.numLanes());
    const laneGap = QUEUE_LANE_GAP;
    const totalLanesWidth = this.queue.numLanes() * laneWidth + (this.queue.numLanes() - 1) * laneGap;
    const lanesStartX = (GAME_WIDTH - totalLanesWidth) / 2;
    const laneY = getQueueY();

    for (const { attacker, orbIndex, lane } of revealed) {
      const key = `${attacker.id}:${orbIndex}`;

      // Compute orb position for particle effects
      const orbLaneX = lanesStartX + lane * (laneWidth + laneGap) + laneWidth / 2;
      const orbY = laneY + 32; // front row Y

      // Find the newly-created container for this orb in queueLaneSprites
      let targetContainer: Phaser.GameObjects.Container | null = null;
      for (const obj of this.queueLaneSprites) {
        if (!(obj instanceof Phaser.GameObjects.Container)) continue;
        const aid = this.spriteAttackerIds.get(obj);
        if (aid !== attacker.id) continue;
        const oidx = this.spriteOrbIndices.get(obj);
        // For single-orb attackers, orbIndex is undefined; for multi-orb, match the index
        if (attacker.orbs.length > 1) {
          if (oidx !== orbIndex) continue;
        }
        targetContainer = obj;
        break;
      }

      // White flash overlay at the orb position
      const flashX = targetContainer ? targetContainer.x : orbLaneX;
      const flashY = targetContainer ? targetContainer.y : orbY;

      const flash = this.scene.add.graphics();
      flash.fillStyle(0xffffff, 0.85);
      flash.fillCircle(flashX, flashY, ATTACKER_SIZE * 0.7);
      flash.setBlendMode(Phaser.BlendModes.ADD);
      flash.setDepth(10);
      this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 300,
        ease: 'Power2',
        onComplete: () => flash.destroy(),
      });

      // Particle burst in the real orb color
      const realColor = getGameplayColorHex(attacker.orbs[orbIndex].colorIndex);
      const emitter = this.scene.add.particles(flashX, flashY, 'particle', {
        speed: { min: 30, max: 80 },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.8, end: 0 },
        lifespan: 350,
        tint: realColor,
        emitting: false,
      });
      emitter.setDepth(10);
      emitter.explode(8);
      this.scene.time.delayedCall(400, () => emitter.destroy());

      // Pop-in animation on the target container (scale bounce)
      if (targetContainer) {
        const targetScaleX = targetContainer.scaleX;
        const targetScaleY = targetContainer.scaleY;
        targetContainer.setScale(targetScaleX * 0.3, targetScaleY * 0.3);
        targetContainer.setAlpha(0.5);
        this.scene.tweens.add({
          targets: targetContainer,
          scaleX: targetScaleX,
          scaleY: targetScaleY,
          alpha: 1,
          duration: 300,
          ease: 'Back.easeOut',
        });
      }

      // Clean up reveal tracking after animation completes
      this.scene.time.delayedCall(350, () => {
        this.revealingMysteryOrbs.delete(key);
      });
    }

    // Play a subtle sound for the reveal
    soundManager.cubeDestroy();
  }

  /** Rebuild the attacker sprite after a mystery orb is revealed (on conveyor). */
  revealMysteryOrb(attacker: AttackerData): void {
    const oldContainer = this.attackerRenderer.attackerSprites.get(attacker.id);
    if (!oldContainer) return;
    const x = oldContainer.x;
    const y = oldContainer.y;
    const scaleX = oldContainer.scaleX;
    const scaleY = oldContainer.scaleY;
    oldContainer.destroy();

    const newContainer = this.attackerRenderer.createAttackerVisual(x, y, attacker.orbs);
    newContainer.setScale(scaleX, scaleY);
    this.attackerRenderer.attackerSprites.set(attacker.id, newContainer);
  }

  /** Clean up all sprite arrays and sets. Called on scene shutdown. */
  destroy(): void {
    for (const bg of this.laneBgs) bg.destroy();
    this.laneBgs = [];
    this.laneBgsCreated = false;
    this.queueLaneSprites = [];
    this.queueLaneHitAreas = [];
    this.revealingMysteryOrbs.clear();
    this.clearSpriteMeta();
  }
}
